import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import FaceTecSDK from "../../services/faceTecSDK";
import FacetecService from "../../services/facetec";
import { ROUTES } from "../../constants/clientRoutes";
import { PAGES } from "../../constants/localization";
import {
  ACTION_DISPLAY_TYPES,
  DIRECTIVE_TYPE,
} from "../../constants/enums";
import {
  Logout,
  goToNextAuthStep,
  handleFaceCheckSuccess,
  getFacecheckEnrollmentToken,
  disableFaceCheckSessionAutoStart,
} from "../../actions/auth";
import MessageBlock from "../../components/Common/MessageBlock";
import ENV from "../../constants/environment";
import { eventTracker, trackEvent } from "../../services/eventTracker";
import { EVENTS } from "../../constants/events";
import PageBlock from "../../components/Layouts/PageBlock";
import InputSkeletonGroup from "../../components/Common/Loaders/InputSkeleton";
import SuccessFaceCheck from "./SuccessFaceCheck";
import ErrorService from "../../services/errorService";
import { appInsights } from "../../services/appInsights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import FlowScreen from "../../components/Layouts/FlowScreen";
import Action from "../../components/Common/Action";
import { Link } from "react-router-dom";
import i18n from "../../i18n/config";

/**
 * Handles sending face scan data to the API and rendering responses
 */
class FaceCheck extends Component {
  constructor(props) {
    super(props);

    this.state = {
      status: "",
      loading: props.hasAuthenticated,
      error: null,
      errorCode: "",
      errorMessage: "",
      errorTitle: "",
      success: false,
    };

    this.onStartClick = this.onStartClick.bind(this);
    this.goBackToLogin = this.goBackToLogin.bind(this);
    this.renderInitialPage = this.renderInitialPage.bind(this);
    this.getInitTokens = this.getInitTokens.bind(this);
  }

  componentWillUnmount() {
    this._isMounted = false;
    // ugly hack to close whenever the screen goes away
    // specially for clicking the back button in the browser
    const closeFacetecButton = document.getElementById(
      "zoom-user-helper-cancel"
    );
    if (closeFacetecButton) {
      closeFacetecButton.click();
    }
  }

  componentDidMount() {
    const {
      enrollMode,
      livenessSessionToken,
      getFacecheckEnrollmentToken,
      autoStart,
    } = this.props;
    this._isMounted = true;

    trackEvent(EVENTS.LOGIN.FACECHECK_STARTED, {});
    if (enrollMode && !livenessSessionToken) {
      this.setState({ loading: true });
      // get facetec token in case they refreshed the page after auth complete
      getFacecheckEnrollmentToken()
        .catch((error) => {
          eventTracker.track("FaceCheck_Enrollment", {
            success: "false",
            error:
              error.status !== undefined
                ? error.status
                : "failure to get facecheck enrollment token",
          });

          this.setState({
            error: error,
            errorTitle: PAGES.FACECHECK.ERROR_TITLE_GET_TOKEN,
            errorMessage: PAGES.FACECHECK.ERROR_CONTENT,
          });
        })
        .then(() => {
          this.setState({ loading: false }, () => {
            if (autoStart) {
              this.onStartClick();
            }
          });
        });
    } else {
      if (autoStart) {
        this.onStartClick();
      }
    }
  }

  /**
   * Retrieves the initialization data for an authenticated user
   */
  getInitTokens() {
    this.props.getFacecheckEnrollmentToken();
    this.renderInitialPage();
  }

  /**
   * Safely update the state from events
   * outside the react lifecycle
   * @param {*} state
   */
  safeSetState(state) {
    if (this._isMounted) {
      this.setState(state);
    }
  }

  /**
   * Begins the ZoOm session to submit face scan data to the API
   */
  onStartClick() {
    const {
      history,
      handleFaceCheckSuccess,
      livenessSessionToken,
      deviceKey,
      licenseKey,
      loginToken,
      goToNextAuthStep,
      enrollMode,
      disableFaceCheckSessionAutoStart,
    } = this.props;

    disableFaceCheckSessionAutoStart();

    this.setState({
      loading: true,
      loadingSubmitTicket: false,
    });

    FacetecService.startFacetec({
      licenseKey,
      deviceKey,
      livenessSessionToken,
      loginToken,
      onStatusChange: (status) => {
        this.safeSetState({
          status: FaceTecSDK.getFriendlyDescriptionForFaceTecSDKStatus(status),
        });
      },
    })
      .then(async (data) => {
        eventTracker.track(this.props.mixpanelEvent, { success: "true" });

        return handleFaceCheckSuccess(data).then(() => {
          if (enrollMode) {
            this.safeSetState({
              success: true,
              loading: false,
            });
          } else {
            goToNextAuthStep(history);
          }
        });
      })
      .catch((error) => {
        appInsights.trackException({
          exception: new Error('Start FaceTec Error'), severityLevel: SeverityLevel.Error, properties: {
            faceTecErrorMessage: error,
            faceTecSessionId: livenessSessionToken,
            faceTecVersion: FaceTecSDK.version(),
            username: this.props.userData.username,
          },
        });
        eventTracker.track(this.props.mixpanelEvent, {
          success: "false",
          error: error.status !== undefined ? error.status : "unknown",
        });

        let resp = ErrorService.GetFaceCheckError(error);
        this.safeSetState({
          error: resp.error,
          errorCode: resp.code,
          errorTitle: resp.title,
          errorMessage: resp.message,
          loading: false,
        });
      });
  }

  goBackToLogin() {
    if (this.props.enrollMode) {
      Logout();
      window.location = `${ENV.BASE_NAVIGATOR_URL}/${ROUTES.EXTERNAL.LOGOUT}`;
    } else {
      this.props.history.push(ROUTES.AUTH.LOGIN);
    }
  }

  getActions() {
    const { loading } = this.state;
    const { enrollMode } = this.props;

    const actions = [
      {
        loading,
        displayType: ACTION_DISPLAY_TYPES.PRIMARY,
        title: enrollMode
          ? PAGES.FACECHECK.SETUP_FACECHECK_START
          : PAGES.FACECHECK.LOGIN_FACECHECK_START,
        onClick: this.onStartClick,
        className: "btn-lg",
      },
    ];

    return actions.reverse();
  }

  renderInitialPage() {
    this.setState({
      error: null,
    });
  }


  render() {
    let { pageTitle, pageSubTitle, pageContent } = this.props;
    const { error, loading, success } =
      this.state;

    if (!loading && !success) {
      pageTitle = PAGES.FACECHECK.GENERIC;
    }

    return (
      <FlowScreen
        className="v3"
        flowTitle={pageTitle}
        showBackButton={true}
      >
        {loading && (
          <div className="container-md">
            <PageBlock>
              <PageBlock.Loader loadingContent={true}>
                <InputSkeletonGroup numInputs={1} />
              </PageBlock.Loader>
            </PageBlock>
          </div>
        )}

        {!loading && !success && (

          <MessageBlock
            className={error ? "max-w-450" : ""}
            actions={error ? [] : this.getActions()}
          >
            {error ? (
              <>
                <h3 className="mb-8">{i18n.t("PAGES.FACECHECK.LOGIN.ERROR_MESSAGE_BLOCK.TITLE")}</h3>
                <p className="marengo-grey mb-8">{i18n.t("PAGES.FACECHECK.LOGIN.ERROR_MESSAGE_BLOCK.DESCRIPTION")}</p>
                <Link to={ROUTES.AUTH.FACECHECK_HELP} className="btn-link no-underline bold">
                  {i18n.t("PAGES.FACECHECK.LOGIN.ERROR_MESSAGE_BLOCK.LINK_TEXT")}
                </Link>
                <Action
                  displayType={ACTION_DISPLAY_TYPES.PRIMARY}
                  title={i18n.t("COMMON.TRY_AGAIN")}
                  onClick={() => this.setState({ showSupportModal: true })}
                  className="btn-lg mt-8"
                />
              </>

            ) : (
              <>
                <h2> {pageSubTitle} </h2>
                <p className="large"> {pageContent} </p>
              </>
            )}
          </MessageBlock>
        )}
        {success && <SuccessFaceCheck />}
      </FlowScreen>

    );
  }
}

FaceCheck.propTypes = {
  /** react-router history */
  history: PropTypes.any,
  /**True for enrollment, false for Login */
  enrollMode: PropTypes.bool,
  pageTitle: PropTypes.string,
  pageSubTitle: PropTypes.string,
  pageContent: PropTypes.string,
  mixpanelEvent: PropTypes.string,
  loginToken: PropTypes.string,
  livenessSessionToken: PropTypes.string,
  deviceKey: PropTypes.string,
  licenseKey: PropTypes.string,
  goToNextAuthStep: PropTypes.func,
  getFacecheckEnrollmentToken: PropTypes.func,
  handleFaceCheckSuccess: PropTypes.func,
  disableFaceCheckSessionAutoStart: PropTypes.func,
  autoStart: PropTypes.bool,
  hasAuthenticated: PropTypes.bool,
  userData: PropTypes.object
};

function mapStateToProps(state) {
  const enrollMode =
    state.auth.authResult.directive.directiveType ===
    DIRECTIVE_TYPE.FaceCheckEnrollment;
  const sessionData = enrollMode
    ? state.auth.authResult.directive
    : state.auth.authResult;

  return {
    enrollMode: enrollMode,
    loginToken: enrollMode ? null : state.auth.authResult.token,
    mixpanelEvent: enrollMode ? "FaceCheck_Enrollment" : "FaceCheck_Login",
    pageTitle: enrollMode
      ? PAGES.FACECHECK.SETUP_FACECHECK_TITLE
      : PAGES.FACECHECK.LOGIN_FACECHECK_TITLE,
    pageSubTitle: enrollMode
      ? PAGES.FACECHECK.SETUP_FACECHECK_SUBTITLE
      : PAGES.FACECHECK.LOGIN_FACECHECK_SUBTITLE,
    pageContent: enrollMode
      ? PAGES.FACECHECK.SETUP_FACECHECK_CONTENT
      : PAGES.FACECHECK.LOGIN_FACECHECK_CONTENT,
    livenessSessionToken: sessionData.livenessSessionToken,
    deviceKey: sessionData.deviceKey,
    licenseKey: sessionData.licenseKey,
    autoStart: state.auth.autoStartFaceCheckSession,
    hasAuthenticated:
      !enrollMode && state.auth.authResult.type === "Authenticated",
    userData: {
      username: state.auth.username
    }
  };
}

export default connect(mapStateToProps, {
  goToNextAuthStep,
  getFacecheckEnrollmentToken,
  handleFaceCheckSuccess,
  disableFaceCheckSessionAutoStart,
})(FaceCheck);